import * as React from "react";

import {cs}                      from "@Common/react/chain-services";
import {consumeContext}          from "@Common/react/context";
import {Link}                    from "@Common/react/cst2";
import {UseState}                from "@Common/react/use-state";
import {Types}                   from "@Common/types";
import {ModalService3}           from "@Common/ui-components/modal-service-3/modal-service-3";
import {LoadingSpinner}          from "@Common/ui-components/photo-dashboard/components/loading-spinner";

import {SelectModal}    from "../../../common/select-modal/select-modal";
import {TwoOptionModal} from "../../../common/two-option-modal/two-option-modal";

export const CancelAction = ({next, onCancelled}:{
    next         : Link<JSX.Element, {show: (params:{item: Types.Apis.Payloads.Next.Item.GetHistory.Item}) => void}>,
    onCancelled ?: (item:Types.Apis.Payloads.Next.Item.GetHistory.Item) => void,
}) => cs(
    consumeContext("session"),
    consumeContext("userLevels"),
    consumeContext("company"),
    ["emulateAgentOfOrder", ({session}, next) => session && (
        next(async ({branchId, agentId}) => {
            await session.applyEmulation({
                branchId,
                ... (agentId === "Branch Order") ? {
                    isBranchOrder: true,
                } : {
                    agentId,
                },
            });

            return true;
        })
    )],
    ["creatorId", ({userLevels, session, company}, next) => session && (
        userLevels.userLevel === 3 ? (
            next(company?.idCompany)
        ) : userLevels.userLevel === 4 ? (
            next(session.data?.branch?.idBranch)
        ) : (
            next(session.data?.agent?.idAgent)
        )
    )],
    ["modalState", (_, next) => UseState({
        initValue: {"status":"confirm"},
        next,
    })],
    ["modal", ({modalState, creatorId, emulateAgentOfOrder}, next) => confirmModal({
        rootNext: next,
        creatorId,
        emulateAgentOfOrder,
        modalState,
        onCancelled,
    })],


    ({modal}) => next((modal)),
);


const confirmModal = ({creatorId, emulateAgentOfOrder, modalState, onCancelled, rootNext}) => cs(
    consumeContext("apis"),
    ["modal", (_, next) => ModalService3({
        xdQaName: "Cancel Action",
        rootNext,
        next,
    })],
    ({apis, modal}) => modalState.value.status === "confirm" ? (
        <TwoOptionModal {...{
            messageLine1: "Order ID: " + modal.params.item.itemId,
            messageLine2: "Are you sure you want to cancel this order?",
            button1Action: () => modal.close(),
            button1Label: "Cancel",
            button2Action: () => modalState.onChange({"status":"cancelReason"}),
            button2Label: "Yes",
        }}/>
    ) : modalState.value.status === "cancelReason" ? (
        <SelectModal {...{
            list: cancellationReasons,
            selectLabel: "Cancellation Reason",
            messageLine1: "Please select why you would like to cancel your order",
            button1Action: () => {
                modalState.onChange({"status":"confirm"});
                modal.close();
            },
            button1Label: "Cancel",
            button2Action: ({selection, otherInput}) => cancel({apis, item: modal.params.item, cancellationReason: otherInput.value ? otherInput.value : selection.value, creatorId, emulateAgentOfOrder, modalState}),
            button2Label: "Continue",
        }}/>
    ) : modalState.value.status === "error" ? (
        <TwoOptionModal {...{
            messageLine1: "Error!",
            messageLine2: modalState.value.message ? modalState.value.message : 'The attempt to cancel of your order named ' + modal.params.item.itemName + ' appears to have failed.',
            messageLine3: modalState.value.message ? '' : "Please try again and contact us if the issue persists.",
            button1Action: () => {
                modalState.onChange({"status":"confirm"});
                modal.close();
            }
        }}/>
    ) : modalState.value.status === "success" ? (
        <TwoOptionModal {...{
            messageLine1: "Your order has been successfully cancelled and deductions will not be made from your billing account.",
            messageLine2: "If your order was purchased using co-pay, voucher, or ad budget funds,",
            messageLine3: "credits will automatically be reapplied to your account",
            button1Action: async () => {
                onCancelled?.(modal.params.item);
                modalState.onChange({"status":"confirm"})
                modal.close();
            },
            button1Label: "OK",
        }}/>
    ) : modalState.value.status === "processing" ? (
        <div className="two-option-modal-5th">
            <div className="message">
                <p className="" xd-qa-type="label" xd-qa-name="Loading">
                    <LoadingSpinner text="Processing..."/>
                </p>
            </div>
        </div>
    ) : modalState.value.status === "reloading" ? (
        <div className="two-option-modal-5th">
            <div className="message">
                <p className="" xd-qa-type="label" xd-qa-name="Loading">
                    <LoadingSpinner text="Orders Reloading..."/>
                </p>
            </div>
        </div>
    ) : (
        null
    )
);

const cancel = async ({apis, item, cancellationReason, creatorId, emulateAgentOfOrder, modalState}) => {
    modalState.onChange({"status":"processing"});
    await emulateAgentOfOrder({branchId: item.branch.idBranch, agentId: item.agent.idAgent});
    const cancelItemJson = {itemId: item.itemId, cancellationReason, creatorId}
    const value = await apis.next.item.cancelItem({cancelItemJson});
    if( value.result === 'failure'){
        modalState.onChange({"status":"error", "message": value?.message})
    }
    else{
        item.transOrderStatus = "cancelled";
        modalState.onChange({"status":"success"})
    }
};

const cancellationReasons = [
    'Duplicate Order / Already Ordered / Not Needed',
    'Incorrect Office/User Selected',
    'Incorrect Payment Type Selected',
    'Incorrect Shipping Address',
    'Incorrect Shipping/Mailing Level Selected',
    'Incorrect Template/Size Selected',
    'Incorrect/Missing Design/Content (Typos, Photo, Grammar)',
    'Incorrect/Missing Future Scheduled/Fulfillment Date',
    'Incorrect/Missing Legal Information',
    'Incorrect/Missing Mailing List',
    'Incorrect/Missing Options (Quantity, Paper-type, Add-ons)',
    'Incorrect/Missing Proof',
    'Missing Promo Code',
    'Missing Store Credit/Voucher/Add Budget',
    'Order Not Approved',
]
